// @ts-nocheck
/**
 * "Trends" tab body for the FinalMappingCard — the post-agent P&L ratio view.
 *
 * Lazy-fetches GET /agent/sessions/{id}/ratios (computed server-side from the
 * persisted mapping; see src/pl_ratios) and renders the Pareto set of ratios
 * as trends: a period-scrubbable headline ledger (inline sparkline per metric,
 * one shared hovered-period state for the whole tab, resting on the FIRST
 * period), a margins line chart, a period-over-period growth chart, and the
 * common-size table. Hovering the ledger or either chart scrubs everything to
 * the same period. Null ratio values (guarded denominators) render as "—" and
 * break the line. Visual language matches the FinalMappingCard / app design
 * system (CSS custom properties, recharts as in QoEWorkbench's
 * TrendDiagnostics).
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { scaleSymlog } from 'victory-vendor/d3-scale';
import { AlertTriangle, HelpCircle, Loader2, TrendingUp } from 'lucide-react';

import { getAgentRatios } from '../../utils/agentApi';
import './RatiosTab.css';

const fmtPct = (v) =>
  v == null || Number.isNaN(v) ? '—' : `${(v * 100).toFixed(1)}%`;
const fmtX = (v) => (v == null || Number.isNaN(v) ? '—' : `${v.toFixed(1)}×`);
const pctTick = (v) => `${(v * 100).toFixed(0)}%`;

// Diverging red↔neutral↔green color for a fraction value. tanh gives a
// saturating response so values well beyond ±100% keep intensifying without
// ever clipping (±100% → ±0.76, ±300% → ±0.995): 0 → muted gray, positive →
// green (more vivid the higher), negative → red (deeper the lower). Lightness
// stays in a readable band on the light panel; the global dark-mode filter
// preserves the hue. Applied only to the profitability rows (margins + CAGR).
const valueColor = (v) => {
  if (v == null || Number.isNaN(v)) return undefined;
  const t = Math.tanh(v);
  const mag = Math.abs(t);
  const hue = t >= 0 ? 146 : 5; // green for ≥0, red for <0
  const sat = Math.round(14 + mag * 78); // 14%..92% — muted near 0, vivid at extremes
  const light = Math.round(43 - mag * 7); // 43%..36% — readable on white
  return `hsl(${hue}, ${sat}%, ${light}%)`;
};

const MARGIN_SERIES = [
  { key: 'gross_margin', name: 'Gross', color: '#10b981' },
  { key: 'ebitda_margin', name: 'EBITDA', color: '#3b82f6' },
  { key: 'operating_margin', name: 'Operating', color: '#8b5cf6' },
  { key: 'net_margin', name: 'Net', color: '#f59e0b' },
];
const GROWTH_SERIES = [
  { key: 'revenue_growth', name: 'Revenue growth', color: '#10b981' },
  { key: 'ebitda_growth', name: 'EBITDA growth', color: '#3b82f6' },
];

// X-axis label budget. At or under this many periods, every period label is
// shown (small sheets render exactly as before). Above it, labels are thinned
// to roughly this many, evenly spaced. Index-based only, so it never parses or
// interprets a period label, and works identically for any label format.
const X_AXIS_TICK_BUDGET = 24;

// Ledger metrics that carry a full per-period series in `result.ratios`.
// Revenue CAGR is deliberately absent: it is a single whole-window number.
const LEDGER_SERIES_KEYS = [
  'gross_margin',
  'ebitda_margin',
  'operating_margin',
  'net_margin',
  'interest_coverage',
  'effective_tax_rate',
];

// Sparkline geometry. viewBox units equal on-screen px (width/height attrs
// match the viewBox), so the row scrub handler can do its mouse math in the
// same units.
const SPARK_W = 140;
const SPARK_H = 26;
const SPARK_PAD = 3;

// Purely presentational sparkline: polyline segments split at null points
// (guarded denominators leave gaps, same rule as the big charts), plus a dot
// at the shared active period index. Scrubbing is handled by the ledger row,
// not here, so vertical mouse travel between rows never drops the hover.
const Sparkline = ({ values, color, activeIdx }) => {
  const n = values.length;
  const finite = values.filter((v) => v != null && !Number.isNaN(v));
  const xAt = (i) =>
    n > 1 ? SPARK_PAD + (i * (SPARK_W - 2 * SPARK_PAD)) / (n - 1) : SPARK_W / 2;
  let yAt = () => SPARK_H / 2;
  if (finite.length) {
    const min = Math.min(...finite);
    const max = Math.max(...finite);
    const span = max - min;
    yAt = (v) =>
      span === 0
        ? SPARK_H / 2
        : SPARK_H - SPARK_PAD - ((v - min) / span) * (SPARK_H - 2 * SPARK_PAD);
  }
  const segments = [];
  let cur = [];
  values.forEach((v, i) => {
    if (v == null || Number.isNaN(v)) {
      if (cur.length) segments.push(cur);
      cur = [];
    } else {
      cur.push({ x: xAt(i), y: yAt(v) });
    }
  });
  if (cur.length) segments.push(cur);

  const active = values[activeIdx];
  return (
    <svg
      className="rt-spark"
      width={SPARK_W}
      height={SPARK_H}
      viewBox={`0 0 ${SPARK_W} ${SPARK_H}`}
      aria-hidden="true"
    >
      {finite.length === 0 && (
        <line
          x1={SPARK_PAD}
          x2={SPARK_W - SPARK_PAD}
          y1={SPARK_H / 2}
          y2={SPARK_H / 2}
          stroke={color}
          strokeDasharray="2 3"
          opacity={0.3}
        />
      )}
      {segments.map((pts, si) =>
        pts.length > 1 ? (
          <polyline
            key={si}
            points={pts.map((p) => `${p.x},${p.y}`).join(' ')}
            fill="none"
            stroke={color}
            strokeWidth={1.5}
            strokeLinejoin="round"
            strokeLinecap="round"
            opacity={0.8}
          />
        ) : (
          <circle key={si} cx={pts[0].x} cy={pts[0].y} r={1.5} fill={color} opacity={0.8} />
        ),
      )}
      {active != null && !Number.isNaN(active) && (
        <circle cx={xAt(activeIdx)} cy={yAt(active)} r={2.5} fill={color} />
      )}
    </svg>
  );
};

const TOOLTIP_STYLE = {
  background: 'var(--panel)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  fontSize: 12,
  color: 'var(--text)',
};

function buildRows(result, keys) {
  return result.periods.map((p) => {
    const row = { period: p.display_name };
    for (const k of keys) {
      const pt = (result.ratios[k] || []).find(
        (x) => x.period_index === p.period_index,
      );
      row[k] = pt && pt.value != null ? pt.value : null;
    }
    return row;
  });
}

const RatiosTab = ({ sessionId }) => {
  const [state, setState] = useState({ status: 'loading', data: null, error: null });
  // Shared hovered-period index for the whole tab (ledger + both charts).
  // null = resting, which pins the ledger to the FIRST period — the earliest
  // period is the baseline, not the last.
  const [hoverIdx, setHoverIdx] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading', data: null, error: null });
    setHoverIdx(null);
    getAgentRatios(sessionId)
      .then((d) => {
        if (!cancelled) setState({ status: 'ready', data: d, error: null });
      })
      .catch((err) => {
        if (!cancelled)
          setState({ status: 'error', data: null, error: err.message });
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const marginRows = useMemo(
    () => (state.data ? buildRows(state.data, MARGIN_SERIES.map((s) => s.key)) : []),
    [state.data],
  );
  const growthRows = useMemo(
    () => (state.data ? buildRows(state.data, GROWTH_SERIES.map((s) => s.key)) : []),
    [state.data],
  );
  // Per-period value arrays aligned to result.periods, one per ledger metric.
  // The ledger reads the shared active index into these.
  const seriesByKey = useMemo(() => {
    if (!state.data) return {};
    const out = {};
    for (const k of LEDGER_SERIES_KEYS) {
      out[k] = state.data.periods.map((p) => {
        const pt = (state.data.ratios[k] || []).find(
          (x) => x.period_index === p.period_index,
        );
        return pt && pt.value != null ? pt.value : null;
      });
    }
    return out;
  }, [state.data]);

  if (state.status === 'loading') {
    return (
      <div className="rt-state">
        <Loader2 size={16} className="rt-spin" />
        <span>Computing ratios…</span>
      </div>
    );
  }
  if (state.status === 'error') {
    return (
      <div className="rt-state rt-state-error">
        <AlertTriangle size={16} />
        <span>{state.error || 'Could not load ratios.'}</span>
      </div>
    );
  }

  const r = state.data;
  if (!r || r.insufficient_data || !r.periods || r.periods.length === 0) {
    return (
      <div className="rt-state">
        <AlertTriangle size={16} />
        <span>Not enough data to compute ratios for this mapping.</span>
      </div>
    );
  }

  const recon = r.reconciliation || {};
  const reconciled = recon.status === 'pass' || recon.status === 'converged';
  const h = r.headline || {};

  // Thin the x-axis labels once a sheet runs long. Recharts' `interval` is a
  // skip count (0 = show every tick, 2 = show every 3rd, ...). Below the budget
  // we keep interval 0 so short sheets are unchanged; above it we step so only
  // ~X_AXIS_TICK_BUDGET labels render. A ~70-period sheet resolves to every 3rd.
  const n = r.periods.length;
  const xTickInterval =
    n > X_AXIS_TICK_BUDGET ? Math.ceil(n / X_AXIS_TICK_BUDGET) - 1 : 0;

  // Clamped so a stale hover index can never outrun a freshly loaded, shorter
  // period list.
  const activeIdx = Math.min(hoverIdx != null ? hoverIdx : 0, n - 1);
  const activePeriodName = r.periods[activeIdx].display_name;
  const clearHover = () => setHoverIdx(null);

  // Row-level scrub: mouse x is mapped onto the sparkline's plot band (all
  // rows share one x-scale, so any row scrubs every row). The ±12px guard
  // keeps the label and value zones inert — reading a value must not move it.
  const scrubFromRow = (e) => {
    const svg = e.currentTarget.querySelector('.rt-spark');
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    if (rect.width === 0) return;
    if (e.clientX < rect.left - 12 || e.clientX > rect.right + 12) return;
    const frac =
      (e.clientX - rect.left - SPARK_PAD) /
      Math.max(1, rect.width - 2 * SPARK_PAD);
    setHoverIdx(Math.max(0, Math.min(n - 1, Math.round(frac * (n - 1)))));
  };

  // Chart→ledger sync. Recharts v3 hands mouse handlers the chart state;
  // activeTooltipIndex is preferred, activeLabel (the period display name)
  // is the fallback.
  const syncFromChart = (st) => {
    if (!st) return;
    const byIdx = Number(st.activeTooltipIndex);
    if (Number.isInteger(byIdx) && byIdx >= 0 && byIdx < n) {
      setHoverIdx(byIdx);
      return;
    }
    if (st.activeLabel != null) {
      const j = r.periods.findIndex((p) => p.display_name === st.activeLabel);
      if (j >= 0) setHoverIdx(j);
    }
  };

  // Each row reads its per-period series at the shared activeIdx; `key: null`
  // rows (Revenue CAGR) are whole-window scalars unaffected by the scrub.
  // `colored` rows get the red↔green scale; interest coverage and effective
  // tax stay neutral (higher isn't "better" / different unit). Spark colors
  // match the chart legend where the metric appears there.
  const stats = [
    {
      key: 'gross_margin',
      label: 'Gross margin',
      fmt: fmtPct,
      colored: true,
      spark: '#10b981',
      tip: 'Revenue left after the direct cost of sales — the core read on pricing power and direct-cost control. A falling trend signals input-cost inflation or pricing pressure.',
    },
    {
      key: 'ebitda_margin',
      label: 'EBITDA margin',
      fmt: fmtPct,
      colored: true,
      spark: '#3b82f6',
      tip: 'Core operating earnings as a share of revenue, before financing, tax, and depreciation. The headline profitability measure used in valuation.',
    },
    {
      key: 'operating_margin',
      label: 'Operating margin',
      fmt: fmtPct,
      colored: true,
      spark: '#8b5cf6',
      tip: 'Profit after depreciation & amortization. A wide gap below EBITDA margin means the business is capital-intensive.',
    },
    {
      key: 'net_margin',
      label: 'Net margin',
      fmt: fmtPct,
      colored: true,
      spark: '#f59e0b',
      tip: 'Bottom-line profit per revenue dollar, after all costs, financing, and tax. Weak net but healthy operating margin points to a heavy interest or tax burden.',
    },
    {
      key: null,
      label: 'Revenue CAGR',
      value: h.revenue_cagr,
      fmt: fmtPct,
      colored: true,
      tip: 'Smoothed annual revenue growth across the full period, stripping out single-period noise. Shown only when periods carry calendar dates.',
    },
    {
      key: 'interest_coverage',
      label: 'Interest coverage',
      fmt: fmtX,
      colored: false,
      spark: '#94a3b8',
      tip: 'How many times EBITDA covers interest expense. A low or falling ratio signals the business is stretched by its borrowing; “—” means no interest.',
    },
    {
      key: 'effective_tax_rate',
      label: 'Effective tax',
      fmt: fmtPct,
      colored: false,
      spark: '#94a3b8',
      tip: 'Share of pre-tax profit paid in tax. An unusually low or erratic rate can flag loss carryforwards, credits, or a pass-through structure.',
    },
  ];

  return (
    <div className="rt-wrap">
      {!reconciled && (
        <div className="rt-note">
          <AlertTriangle size={13} />
          <span>
            These ratios are computed from a mapping that didn’t fully reconcile
            to source Net Income — treat the bottom-line figures with care.
          </span>
        </div>
      )}

      <div className="rt-ledger-block">
        <div className="rt-period-pill">{activePeriodName}</div>
        <div className="rt-ledger" onMouseLeave={clearHover}>
          {stats.map((t) => {
            const series = t.key ? seriesByKey[t.key] || [] : null;
            const raw = series ? series[activeIdx] : t.value;
            return (
              <div
                className="rt-ledger-row"
                key={t.label}
                onMouseMove={series ? scrubFromRow : undefined}
              >
                <span className="rt-ledger-label">
                  {t.label}
                  <span
                    className="rt-help"
                    data-tip={t.tip}
                    role="img"
                    aria-label={`${t.label}: ${t.tip}`}
                    tabIndex={0}
                  >
                    <HelpCircle size={12} />
                  </span>
                </span>
                {series ? (
                  <Sparkline values={series} color={t.spark} activeIdx={activeIdx} />
                ) : (
                  <span className="rt-spark-empty" />
                )}
                <span
                  className="rt-ledger-value"
                  style={
                    t.colored && typeof raw === 'number'
                      ? { color: valueColor(raw) }
                      : undefined
                  }
                >
                  {t.fmt(raw)}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="rt-chart-card">
        <h4 className="rt-chart-title">
          <TrendingUp size={13} /> Margins
        </h4>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart
            data={marginRows}
            margin={{ top: 8, right: 16, left: 0, bottom: 40 }}
            onMouseMove={syncFromChart}
            onMouseLeave={clearHover}
          >
            <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
            <XAxis
              dataKey="period"
              tick={{ fontSize: 10, fill: 'var(--text)' }}
              interval={xTickInterval}
              angle={-30}
              textAnchor="end"
              height={50}
            />
            <YAxis tick={{ fontSize: 10, fill: 'var(--text)' }} tickFormatter={pctTick} width={48} />
            {/* The shared-hover reference line doubles as the tooltip cursor,
                so the native cursor is off to avoid a doubled line. */}
            <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => fmtPct(v)} cursor={false} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {hoverIdx != null && (
              <ReferenceLine x={activePeriodName} stroke="#94a3b8" strokeDasharray="3 3" />
            )}
            {MARGIN_SERIES.map((s) => (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.name}
                stroke={s.color}
                strokeWidth={2}
                dot={false}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {r.periods.length >= 2 && (
        <div className="rt-chart-card">
          <h4 className="rt-chart-title">
            <TrendingUp size={13} /> Growth (period over period)
          </h4>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart
              data={growthRows}
              margin={{ top: 8, right: 16, left: 0, bottom: 40 }}
              onMouseMove={syncFromChart}
              onMouseLeave={clearHover}
            >
              <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
              <XAxis
                dataKey="period"
                tick={{ fontSize: 10, fill: 'var(--text)' }}
                interval={xTickInterval}
                angle={-30}
                textAnchor="end"
                height={50}
              />
              {/* Symmetric-log y-axis: linear near 0, logarithmic in the tails.
                  A volatile outlier (e.g. +4,481% growth off a near-zero prior
                  month) compresses instead of flattening the normal ±10-50%
                  band, and negatives/zero are handled natively. constant() sets
                  the half-width of the linear region — smaller = more resolution
                  for small values. The tooltip + headline still show the true %. */}
              <YAxis
                scale={scaleSymlog().constant(0.1)}
                domain={['auto', 'auto']}
                allowDataOverflow={false}
                tick={{ fontSize: 10, fill: 'var(--text)' }}
                tickFormatter={pctTick}
                width={52}
              />
              <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => fmtPct(v)} cursor={false} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {hoverIdx != null && (
                <ReferenceLine x={activePeriodName} stroke="#94a3b8" strokeDasharray="3 3" />
              )}
              {GROWTH_SERIES.map((s) => (
                <Line
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  name={s.name}
                  stroke={s.color}
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="rt-chart-card">
        <h4 className="rt-chart-title">Common-size (% of revenue)</h4>
        <div className="rt-table-wrap">
          <table className="rt-table">
            <thead>
              <tr>
                <th className="rt-col-account">Account</th>
                {r.common_size.periods.map((p, i) => (
                  <th
                    key={p.period_index}
                    className={`rt-num${hoverIdx === i ? ' rt-hl-col' : ''}`}
                  >
                    {p.display_name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {r.common_size.rows.map((row) => (
                <tr key={row.row_index}>
                  <td className="rt-account" title={row.account_name || ''}>
                    {row.account_name || '—'}
                  </td>
                  {row.values.map((v, i) => (
                    <td
                      key={i}
                      className={`rt-num${hoverIdx === i ? ' rt-hl-col' : ''}`}
                    >
                      {fmtPct(v)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {(r.notes || []).length > 0 && (
        <div className="rt-footnotes">
          {r.notes.map((n, i) => (
            <div key={i}>· {n}</div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RatiosTab;
